// combatpro/views/LegendaryView.js
//
// Stage 33: view-legendary shell. Stage 70 (Legendary Actions View Foundation)
// migrates the legacy per-monster Legendary Actions card (battletracker.html
// legendary-block, 6090-6163, renderer refreshLegendaryActions 8369, tracker
// updateLegendaryTracker 8406, use/reset/config 8426-8453, add/remove 8302-8367) for
// the ACTIVE monster.
//
// The legacy built one legendary-block-${i} per monster inside createMonsterPanel and
// reparented the active one into #vi-legendary-mount via vi_mountLegendary (10228).
// React renders the active monster's card directly from state.currentCombatantIndex
// (the established active-monster view pattern) - no reparenting, no getElementById.
//
// Faithful migration (master spec preserved verbatim):
//   - the header (👑 Legendary Actions), the tracker (remaining/max, colour-coded), a
//     Reset button, and four static help paragraphs (6091-6116).
//   - the config grid (6119-6158): Max Actions number input (1-10) and Reset Rate
//     select (turn/round/short/long/day/manual), plus five static rate helper cards.
//   - the list (refreshLegendaryActions 8369): empty -> "No legendary actions"; else
//     cards with name, "Cost: {cost}", desc, a "⚡ Use Action" button (disabled when
//     remaining < cost) and a Remove button.
//   - the "+ Add Legendary Action" picker (addLegendaryAction 8302 /
//     confirmAddLegendaryAction 8327): a select of [official DB + view-local customs]
//     (filtered to entries with a name) by index, label
//     "name (Cost: cost) - desc.substring(0,40)...", plus a custom sentinel; Add
//     resolves the choice and commits, Cancel closes. Custom-add collects name
//     (required), cost (parseInt||1), desc via three window.prompts.
//
// OWNERSHIP (determined from discovery, not assumed): maxLegendaryActions,
// usedLegendaryActions, and legendaryResetRate are persistent MONSTER fields (factory
// 5005-5008), not encounter state. "remaining" is DERIVED (max - used), never stored.
// Use / Reset / Max / Reset Rate are self-contained tracker/config mutations (NOT the
// deferred combat-rolling subsystem), so they are fully migrated here through the Stage
// 70 CombatContext handlers (addMonsterLegendaryAction, removeMonsterLegendaryAction,
// useMonsterLegendaryAction, resetMonsterLegendaryActions, updateMonsterLegendaryConfig).
// The official action database is reused from window.VI_LEGENDARY_ACTIONS_DB (the same
// window.VI_* convention) - not re-bundled; if unavailable the picker degrades to custom
// entries plus the custom sentinel. View-local state owns only UI concerns: picker
// visibility + selected value and the session custom-action accumulator. Custom-action
// entry keeps the legacy window.prompt interaction; removal keeps window.confirm. The
// legacy reset-rate toast notification is a DEFERRED DOM side effect (no functional
// effect; no toast system is introduced this stage). No DOM manipulation, no
// getElementById, no createElement, no innerHTML, no new runtime, no CombatContext
// ownership change. No optional chaining. No em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';

// Read the official legendary-actions database via the same window.VI_* convention.
// Graceful degradation: missing / non-array -> [].
function readLegendaryDb() {
  return (typeof window !== 'undefined' && Array.isArray(window.VI_LEGENDARY_ACTIONS_DB)) ? window.VI_LEGENDARY_ACTIONS_DB : [];
}

function LegendaryView() {
  const {
    state,
    addMonsterLegendaryAction,
    removeMonsterLegendaryAction,
    useMonsterLegendaryAction,
    resetMonsterLegendaryActions,
    updateMonsterLegendaryConfig
  } = useContext(CombatContext);
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];

  // View-local UI state (not shared application state).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState('');
  const [customActions, setCustomActions] = useState([]);

  if (!monster) {
    return (
      <div className="vi-view" id="view-legendary">
        <div id="vi-legendary-mount"></div>
      </div>
    );
  }

  const actions = Array.isArray(monster.legendaryActions) ? monster.legendaryActions : [];
  const db = readLegendaryDb();
  const max = (monster.maxLegendaryActions || 3);
  const used = (monster.usedLegendaryActions || 0);
  const remaining = max - used;
  const resetRate = (monster.legendaryResetRate || 'turn');

  // Tracker colour thresholds (updateLegendaryTracker 8417-8423).
  var trackerColor;
  if (remaining === max) {
    trackerColor = 'rgba(134,239,172,0.9)';
  } else if (remaining > 0) {
    trackerColor = 'var(--gold-bright)';
  } else {
    trackerColor = 'rgba(252,165,165,0.9)';
  }

  // Picker pool: [official DB + custom] filtered to entries with a name, addressed by
  // index (addLegendaryAction 8303-8305 / confirmAddLegendaryAction 8349-8350).
  const allForPicker = db.concat(customActions).filter(function(la) { return la && la.name; });

  function openPicker() {
    setPickerValue(allForPicker.length ? '0' : '__CUSTOM__');
    setPickerOpen(true);
  }

  function closePicker() {
    setPickerOpen(false);
  }

  function confirmAdd() {
    var value = pickerValue;
    if (value === '__CUSTOM__') {
      var name = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Legendary Action name:') : null;
      if (!name || !name.trim()) { closePicker(); return; }
      var cost = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Action cost (1-3):') : null;
      var desc = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Description:') : null;
      var newAction = {
        name: name.trim(),
        cost: parseInt(cost, 10) || 1,
        desc: desc || ''
      };
      setCustomActions(function(prev) { return prev.concat([newAction]); });
      addMonsterLegendaryAction(index, newAction);
    } else {
      var selected = allForPicker[parseInt(value, 10)];
      if (selected) { addMonsterLegendaryAction(index, selected); }
    }
    closePicker();
  }

  function onRemove(actionIndex) {
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Remove this legendary action?')) { return; }
    removeMonsterLegendaryAction(index, actionIndex);
  }

  function onUse(cost) {
    // useMonsterLegendaryAction is a CombatContext action (a setState closure), not a
    // React hook; the 'use' prefix triggers a false positive here.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMonsterLegendaryAction(index, cost);
  }

  function onReset() {
    resetMonsterLegendaryActions(index);
  }

  function onMaxChange(e) {
    updateMonsterLegendaryConfig(index, 'maxLegendaryActions', parseInt(e.target.value, 10));
  }

  function onResetRateChange(e) {
    // Mirrors updateLegendaryResetRate state write (8452). The legacy toast
    // notification (8465-8484) is a deferred DOM side effect, not migrated here.
    updateMonsterLegendaryConfig(index, 'legendaryResetRate', e.target.value);
  }

  return (
    <div className="vi-view" id="view-legendary">
      <div id="vi-legendary-mount">
        <div id={'legendary-block-' + index}>
          <div className="vi-legendary-header">
            <div className="vi-legendary-header-row">
              <h3>👑 Legendary Actions</h3>
              <div className="vi-flex-center vi-gap-10">
                <span id={'legendary-tracker-' + index} className="vi-legendary-tracker" style={{ color: trackerColor }}>{remaining + '/' + max}</span>
                <button type="button" onClick={onReset} className="vi-legendary-reset-btn">Reset</button>
              </div>
            </div>
            <div className="legendary-help-text">
              <p>
                Legendary Actions allow powerful creatures to act outside of their normal turn during combat.
                This keeps them active throughout the round and prevents them from being overwhelmed by multiple players.
              </p>
              <p>
                Use this section to assign and manage the creature's available Legendary Actions.
                These actions are typically used by the Game Master at the end of another creature's turn.
              </p>
              <p>
                The counter shows how many Legendary Actions are currently available.
                Most creatures can use up to 3 per round, but this can vary depending on the encounter.
              </p>
              <p>
                Use the Reset button at the start of each new round to restore the creature's Legendary Actions
                so they can be used again.
              </p>
            </div>
          </div>
          <div className="vi-section-body">
            <div className="vi-legendary-config">
              <div className="vi-legendary-config-grid">
                <label className="vi-label-sm">Max Actions:</label>
                <input
                  type="number"
                  id={'maxLegendary-' + index}
                  value={max}
                  min="1"
                  max="10"
                  onChange={onMaxChange}
                  className="vi-legendary-max-input"
                />

                <label className="vi-label-sm">Reset Rate:</label>
                <select id={'legendaryResetRate-' + index} value={resetRate} onChange={onResetRateChange} className="vi-legendary-rate-select">
                  <option value="turn">Every Turn</option>
                  <option value="round">Every Round</option>
                  <option value="short">Short Rest</option>
                  <option value="long">Long Rest</option>
                  <option value="day">Per Day</option>
                  <option value="manual">Manual Only</option>
                </select>
              </div>

              <div className="vi-legendary-rate-cards">
                <div className="vi-legendary-rate-card">
                  <div className="vi-legendary-rate-card-title">Every Turn</div>
                  <div className="vi-legendary-rate-card-desc">Resets on this creature's turn {'\u2192'} use for fast regenerating bosses</div>
                </div>
                <div className="vi-legendary-rate-card">
                  <div className="vi-legendary-rate-card-title">Every Round</div>
                  <div className="vi-legendary-rate-card-desc">Resets at round start {'\u2192'} use for persistent threats</div>
                </div>
                <div className="vi-legendary-rate-card">
                  <div className="vi-legendary-rate-card-title">Short/Long Rest</div>
                  <div className="vi-legendary-rate-card-desc">Reset manually after rest {'\u2192'} use for attrition encounters</div>
                </div>
                <div className="vi-legendary-rate-card">
                  <div className="vi-legendary-rate-card-title">Per Day</div>
                  <div className="vi-legendary-rate-card-desc">Resets after 24 hours {'\u2192'} use for once-per-day powers</div>
                </div>
                <div className="vi-legendary-rate-card">
                  <div className="vi-legendary-rate-card-title">Manual Only</div>
                  <div className="vi-legendary-rate-card-desc">Only resets via Reset button {'\u2192'} full DM control</div>
                </div>
              </div>
            </div>

            <div id={'legendary-list-' + index} className="vi-mb-10">
              {actions.length === 0 ? (
                <p className="vi-modal-result-text">No legendary actions</p>
              ) : (
                actions.map(function(action, i) {
                  var canUse = remaining >= action.cost;
                  return (
                    <div className="vi-legendary-card" key={i}>
                      <div className="vi-lair-inner">
                        <div className="vi-flex-1">
                          <div className="vi-flex-center vi-gap-10 vi-mb-8">
                            <span className="vi-legendary-name">{action.name}</span>
                            <span className="vi-legendary-cost">{'Cost: ' + action.cost}</span>
                          </div>
                          <div className="vi-legendary-desc">{action.desc}</div>
                          <button
                            type="button"
                            onClick={(function(c) { return function() { onUse(c); }; })(action.cost)}
                            disabled={!canUse}
                            className={'vi-btn vi-btn-sm ' + (canUse ? 'vi-btn-success' : 'vi-btn-secondary')}
                          >
                            {'\u26a1 Use Action'}
                          </button>
                        </div>
                        <button type="button" onClick={(function(ai) { return function() { onRemove(ai); }; })(i)} className="btn-danger vi-btn vi-btn-sm">Remove</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <button type="button" onClick={openPicker} className="btn-primary-gold vi-btn vi-btn-secondary">+ Add Legendary Action</button>
          </div>
        </div>
      </div>

      {pickerOpen ? (
        <div className="legendary-modal-overlay">
          <div className="vi-modal-overlay">
            <div className="vi-modal-box-sm">
              <h3 className="vi-modal-title">Select Legendary Action</h3>
              <select id="legendaryDropdown" className="vi-modal-select" value={pickerValue} onChange={function(e) { setPickerValue(e.target.value); }}>
                {allForPicker.map(function(la, i) {
                  var label = la.name + ' (Cost: ' + la.cost + ') - ' + String(la.desc || '').substring(0, 40) + '...';
                  return <option key={i} value={'' + i}>{label}</option>;
                })}
                <option value="__CUSTOM__">+ Add Custom Legendary Action</option>
              </select>
              <div className="vi-modal-actions">
                <button type="button" onClick={closePicker} className="vi-btn vi-btn-secondary">Cancel</button>
                <button type="button" onClick={confirmAdd} className="btn-primary-gold vi-btn">Add</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default LegendaryView;
